import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';

import { withTranslations } from '../../utils/withTranslations';
import { StyledTaskCard } from '../TaskReviewCard';

import { TranscriptHeader } from './Header/TranscriptHeader';
import { TranscriptSentences } from './Sentences/TranscriptSentences';
import { TranscriptAI } from './TranscriptAI';
import { TranscriptButtons } from './TranscriptButtons';
import { TranscriptScrollButton } from './TranscriptScrollButton';
import { Sentences, Transcription as TranscriptionProps } from './transcriptTypes';

export interface TranscriptProps {
  transcriptionSentences?: Sentences[];
  playedSeconds: number;
  transcription: TranscriptionProps | undefined;
  fillerWords: string[] | undefined;
  fillerWordsPercent: string | undefined;
  onSeekTo: (value: number) => void;
  highEnergy: string | undefined;
  transcriptionStatus: string;
}

export const Transcript = withTranslations(
  ({
    transcriptionSentences,
    playedSeconds,
    transcription,
    fillerWords,
    fillerWordsPercent,
    onSeekTo,
    highEnergy,
    transcriptionStatus,
  }: TranscriptProps) => {
    const [userScrolled, setUserScrolled] = useState(false);
    const [activeTranscription, setActiveTranscription] = useState(0);
    const { t } = useTranslation('components', { keyPrefix: 'transcript' });

    const sentences = useMemo(() => {
      switch (activeTranscription) {
        case 0:
          return transcriptionSentences;
        case 1:
          return transcription?.chapters;
        case 2:
          return transcription?.sentiment_analysis_results;
        case 3:
          return transcription?.auto_highlights_result.results;
        default:
          return [];
      }
    }, [transcription, transcriptionSentences, activeTranscription]);

    const content = useMemo(() => {
      switch (transcriptionStatus) {
        case 'COMPLETED':
          return (
            <Box>
              {transcriptionSentences?.length ? (
                <>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mr={4}>
                    <TranscriptButtons
                      activeTranscription={activeTranscription}
                      onSetActiveTranscription={setActiveTranscription}
                    />
                    {userScrolled && activeTranscription !== 3 && (
                      <TranscriptScrollButton onSetUserScrolled={setUserScrolled} />
                    )}
                  </Box>
                  <TranscriptHeader
                    activeTranscription={activeTranscription}
                    fillerWordsPercent={fillerWordsPercent}
                    highEnergy={highEnergy}
                  />
                  <Box overflow="auto" pr={2}>
                    <Box overflow="auto" maxHeight={264} minHeight={264} pr={2}>
                      <TranscriptSentences
                        playedSeconds={playedSeconds}
                        fillerWords={fillerWords}
                        onSeekTo={onSeekTo}
                        userScrolled={userScrolled}
                        onSetUserScrolled={setUserScrolled}
                        activeTranscription={activeTranscription}
                        sentences={sentences}
                      />
                    </Box>
                  </Box>
                </>
              ) : (
                <Box my={14}>
                  <Typography variant="text2" textAlign="center" maxWidth="250px" margin="0 auto" display="block">
                    {t('error')}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        case 'ERROR':
          return (
            <Box my={14}>
              <Typography variant="text2" textAlign="center" maxWidth="250px" margin="0 auto" display="block">
                {t('error')}
              </Typography>
            </Box>
          );

        default:
          return (
            <Box my={14}>
              <Typography variant="text2" textAlign="center" maxWidth="250px" margin="0 auto" display="block">
                {t('empty')}
              </Typography>
            </Box>
          );
      }
    }, [
      activeTranscription,
      fillerWords,
      fillerWordsPercent,
      highEnergy,
      onSeekTo,
      playedSeconds,
      sentences,
      transcriptionStatus,
      userScrolled,
      t,
      transcriptionSentences?.length,
    ]);

    return (
      <StyledTaskCard sx={{ pr: 2, position: 'relative' }}>
        <TranscriptAI />
        {content}
      </StyledTaskCard>
    );
  },
);
